import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Spreadsheet } from 'react-spreadsheet'; // Import react-spreadsheet
import * as XLSX from 'xlsx'; // Import XLSX for parsing Excel files

const MAX_PREVIEW_ROWS = 500; // if you also want to limit rows

const PdfPreviewModal = ({
  show,
  pdfPreviewUrls,
  currentPdfIndex,
  setCurrentPdfIndex,
  handleCancel,
  handleFinalPublish
}) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [excelData, setExcelData] = useState([]); // New state to hold Excel data

  // Helper function to check if the URL is an Excel file
  const isExcelFile = (url) => {
    const strippedUrl = url.split('?')[0].toLowerCase();
    return strippedUrl.endsWith('.xlsx') || strippedUrl.endsWith('.xls');
  };

  const currentFile = pdfPreviewUrls.length > 0 ? pdfPreviewUrls[currentPdfIndex] : null;

  // Function to load and parse the Excel file using XLSX
  const loadExcelData = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch (${response.status})`);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0]; // first sheet
      const sheet = workbook.Sheets[sheetName];
      // raw rows as arrays; defval:'' ensures empty cells become ''
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

      console.log("Parsed Excel Data:", jsonData);

      // (Optional) truncate rows if too many:
      let usedRows = jsonData;
      if (jsonData.length > MAX_PREVIEW_ROWS) {
        usedRows = jsonData.slice(0, MAX_PREVIEW_ROWS);
        console.warn(`Truncated to first ${MAX_PREVIEW_ROWS} rows.`);
      }

      // Compute max columns across all usedRows
      const maxCols = usedRows.reduce((m, row) => Math.max(m, row.length), 0);

      // Normalize each row to length maxCols, wrapping cells as {value, readOnly: true}
      const spreadsheetData = usedRows.map(row =>
        Array.from({ length: maxCols }, (_, i) => ({
          value: row[i] !== undefined ? row[i] : '',
          readOnly: true
        }))
      );

      console.log("Formatted Excel Data:", spreadsheetData);
      setExcelData(spreadsheetData);
    } catch (error) {
      console.error('Error loading Excel file:', error);
    }
  };

  // When currentFile changes
  useEffect(() => {
    if (currentFile && isExcelFile(currentFile.url)) {
      loadExcelData(currentFile.url);
    } else {
      setExcelData([]); // clear when not Excel
    }
  }, [currentFile]);

  const handlePublishClick = () => {
    setIsPublishing(true);
    handleFinalPublish();
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      backdrop="static"
      keyboard={false}
      dialogClassName='pdfPreviewModal'
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{currentFile ? `${currentFile.title} Preview` : 'File Preview'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentFile && isExcelFile(currentFile.url) ? (
          <div style={{ maxWidth: '100%', maxHeight: '500px', overflow: 'auto' }}>
            <Spreadsheet data={excelData} />
          </div>
        ) : (
          <iframe
            title="File Preview"
            src={currentFile ? currentFile.url : ''}
            style={{ width: '100%', height: '500px' }}
            frameBorder="0"
          />
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button variant="secondary" onClick={handleCancel} className="me-auto">Close</Button>
        <div className="d-flex justify-content-center">
          <Button
            variant="secondary"
            onClick={() => {
              if (currentPdfIndex > 0) setCurrentPdfIndex(currentPdfIndex - 1);
            }}
            disabled={currentPdfIndex === 0}
            className="mx-2"
          >
            {'<'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              if (currentPdfIndex < pdfPreviewUrls.length - 1) setCurrentPdfIndex(currentPdfIndex + 1);
            }}
            disabled={currentPdfIndex === pdfPreviewUrls.length - 1}
            className="mx-2"
          >
            {'>'}
          </Button>
        </div>
        <Button 
          variant="primary" 
          onClick={handlePublishClick} 
          disabled={isPublishing}
        >
          {isPublishing ? 'Publishing...' : 'Publish Report'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PdfPreviewModal;
